/**
 * GraphicsLibrary – browse, filter, rate, download and manage all creatives (graphics + videos).
 */
import { useState, useCallback, useRef, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiRequest, getAuthToken } from "@/lib/queryClient";
import { summarizeImagePrompt } from "@shared/image-prompt-summary";
import { useJobPolling } from "@/hooks/use-job-polling";
import { useToast } from "@/hooks/use-toast";
import { FriendlyErrorMapper } from "@/lib/errors/FriendlyErrorMapper";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import {
  Trash2,
  Download,
  ThumbsUp,
  ThumbsDown,
  ChevronLeft,
  ChevronRight,
  HardDrive,
  ImageOff,
  X,
  Loader2,
  Pencil,
  RefreshCw,
  Upload,
  Image,
  Film,
  Cloud,
  GalleryVertical,
  Pin,
  PinOff,
  Layers,
} from "lucide-react";
import CloudStoragePicker from "@/components/cloud-storage-picker";
import { useAuth } from "@/hooks/useAuth";
import {
  GALLERY_CATEGORIES,
  GALLERY_CATEGORY_LABEL_KEYS,
  type GalleryCategory,
} from "@/components/viral-gallery/types";
import SequenceCard, { type SequenceListItem } from "@/components/stories-creator/sequence-card";
import SequenceDetailModal from "@/components/stories-creator/sequence-detail-modal";
import { Skeleton } from "@/components/ui/skeleton";
import { MediaGridSkeleton } from "@/components/skeletons";

type MediaType = "image" | "video";

interface UserGraphic {
  id: string;
  filename: string;
  format: string;
  variantIndex: number;
  sessionId: string | null; // VP-88: shared across bulk-uploaded format variants of one creative
  width: number;
  height: number;
  fileSizeBytes: number;
  prompt: string | null;
  imageModel: string | null;
  funnelType: string | null;
  tags: string[];
  notes: string | null;
  rating: "up" | "down" | null;
  ratingReason: string | null;
  campaignIds: string[];
  mediaType: MediaType | null;
  isUserUpload: boolean | null;
  isInGallery: boolean | null;
  galleryCategory: GalleryCategory | null;
  createdAt: string;
  signedUrl: string | null;
  source?: "graphic" | "asset";
}

interface LibraryResponse {
  items: UserGraphic[];
  total: number;
  page: number;
  pageSize: number;
}

interface QuotaResponse {
  usedBytes: number;
  quotaBytes: number;
  graphicsCount: number;
  mediaAssetsCount?: number;
  totalAssetCount?: number;
}

const FORMAT_LABELS: Record<string, string> = {
  feed: "formats.feed",
  story: "formats.story",
  square: "formats.square",
  landscape: "formats.landscape",
  banner: "formats.banner",
  video: "formats.video",
  custom: "formats.custom",
};

const FORMAT_RATIOS: Record<string, string> = {
  feed: "4:5",
  story: "9:16",
  square: "1:1",
  landscape: "16:9",
  banner: "1.91:1",
};

const MEDIA_TYPE_LABELS: Record<string, string> = {
  all: "Wszystkie typy",
  image: "Grafiki",
  video: "Wideo",
};

/**
 * Force a download for cross-origin URLs (R2/CDN). The native `<a download>`
 * attribute is ignored by browsers when href points to a different origin —
 * the link just navigates and the image opens inline. Fetching the file into
 * a Blob and creating a `blob:` URL bypasses that restriction.
 */
async function forceDownload(url: string, filename: string): Promise<void> {
  try {
    const resp = await fetch(url, { credentials: "omit" });
    if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
    const blob = await resp.blob();
    const blobUrl = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = blobUrl;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(blobUrl), 1000);
  } catch {
    // CORS / network failure — fall back to opening in a new tab so the user
    // can still right-click → save.
    window.open(url, "_blank", "noopener,noreferrer");
  }
}

function isVideoFile(graphic: UserGraphic): boolean {
  return graphic.mediaType === "video" || graphic.format === "video";
}

function formatBytes(bytes: number): string {
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(2)} GB`;
}

function QuotaBar({ used, total }: { used: number; total: number }) {
  const pct = Math.min(100, (used / total) * 100);
  const color = pct > 90 ? "bg-red-500" : pct > 70 ? "bg-yellow-500" : "bg-emerald-500";
  return (
    <div className="flex items-center gap-3">
      <HardDrive className="w-4 h-4 text-white/50 flex-shrink-0" />
      <div className="flex-1 bg-white/[0.08] rounded-full h-1.5 overflow-hidden">
        <div
          className={`h-full rounded-full transition-all ${color}`}
          style={{ width: `${pct}%` }}
        />
      </div>
      <span className="text-xs text-white/50 whitespace-nowrap">
        {formatBytes(used)} / {formatBytes(total)}
      </span>
    </div>
  );
}

// VP-88: a creative uploaded in several formats (shared sessionId) shown as one card with a
// format-count badge. Click toggles an inline panel with the individual format variants.
function CreativeGroupCard({
  group,
  active,
  onOpen,
}: {
  group: UserGraphic[];
  active: boolean;
  onOpen: (sessionId: string) => void;
}) {
  const cover = group[0];
  const formats = Array.from(new Set(group.map((g) => g.format)));
  return (
    <button
      type="button"
      onClick={() => cover.sessionId && onOpen(cover.sessionId)}
      className={`group relative aspect-square rounded-lg overflow-hidden border text-left bg-white/5 ${active ? "border-rose-500" : "border-white/10 hover:border-white/25"}`}
    >
      {cover.signedUrl ? (
        <img
          src={cover.signedUrl}
          alt={cover.filename}
          className="w-full h-full object-cover"
          loading="lazy"
        />
      ) : (
        <div className="w-full h-full flex items-center justify-center">
          <ImageOff className="w-6 h-6 text-white/20" />
        </div>
      )}
      <div className="absolute top-1.5 right-1.5 bg-rose-500/90 text-white text-[9px] px-1.5 py-0.5 rounded-full flex items-center gap-0.5">
        <Layers className="w-3 h-3" /> {group.length}
      </div>
      <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/80 to-transparent p-2">
        <div className="text-[11px] text-white font-semibold">{group.length} formatów</div>
        <div className="flex flex-wrap gap-1 mt-1">
          {formats.map((f) => (
            <span key={f} className="text-[9px] px-1 rounded bg-white/15 text-white/80">
              {FORMAT_RATIOS[f] ?? f}
            </span>
          ))}
        </div>
      </div>
    </button>
  );
}

interface GraphicCardProps {
  graphic: UserGraphic;
  selected: boolean;
  onSelect: (id: string) => void;
  onOpen: (g: UserGraphic) => void;
  onDelete: (id: string) => void;
  onRate: (id: string, rating: "up" | "down" | null) => void;
  onEdit: (g: UserGraphic) => void;
  onPinToGallery?: (g: UserGraphic) => void;
  isInGallery?: boolean;
  onUseAsVideoFirstFrame?: (g: UserGraphic) => void;
}

function GraphicCard({
  graphic,
  selected,
  onSelect,
  onOpen,
  onDelete,
  onRate,
  onEdit,
  onPinToGallery,
  isInGallery,
  onUseAsVideoFirstFrame,
}: GraphicCardProps) {
  const { t } = useTranslation("graphicsLibrary");
  const isPortrait = graphic.height > graphic.width;
  const isLandscape = graphic.width > graphic.height;
  const isPipelineAsset = graphic.source === "asset";

  return (
    <div
      className={`group relative rounded-xl overflow-hidden border transition-all cursor-pointer ${
        selected
          ? "border-red-500 ring-2 ring-red-500/30"
          : "border-white/[0.08] hover:border-white/20"
      } bg-gray-900`}
    >
      {/* Selection checkbox — only for library graphics */}
      {!isPipelineAsset && (
        <div
          className="absolute top-2 left-2 z-10"
          onClick={(e) => {
            e.stopPropagation();
            onSelect(graphic.id);
          }}
        >
          <div
            className={`w-5 h-5 rounded border-2 transition-all flex items-center justify-center ${
              selected
                ? "bg-red-500 border-red-500"
                : "border-white/30 bg-black/40 opacity-0 group-hover:opacity-100"
            }`}
          >
            {selected && <span className="text-white text-xs">✓</span>}
          </div>
        </div>
      )}

      {/* Image/Video area */}
      <div
        className={`relative bg-black/40 flex items-center justify-center overflow-hidden ${
          isPortrait ? "aspect-[3/4]" : isLandscape ? "aspect-[16/9]" : "aspect-square"
        }`}
        onClick={() => onOpen(graphic)}
      >
        {FORMAT_RATIOS[graphic.format] && (
          <span className="absolute top-2 right-2 z-10 px-1.5 py-0.5 rounded-md bg-black/60 backdrop-blur-sm text-[10px] font-mono text-white/90 border border-white/10">
            {FORMAT_RATIOS[graphic.format]}
          </span>
        )}
        {graphic.signedUrl ? (
          isVideoFile(graphic) ? (
            <div className="relative w-full h-full">
              <video
                src={graphic.signedUrl}
                className="w-full h-full object-cover"
                muted
                preload="metadata"
              />
              <div className="absolute inset-0 flex items-center justify-center bg-black/20">
                <Film className="w-8 h-8 text-white/70" />
              </div>
            </div>
          ) : (
            <img
              src={graphic.signedUrl}
              alt={`${graphic.format} v${graphic.variantIndex}`}
              className="w-full h-full object-cover"
              loading="lazy"
            />
          )
        ) : (
          <div className="flex flex-col items-center gap-2 text-white/30">
            <ImageOff className="w-8 h-8" />
            <span className="text-xs">{t("card.noPreview")}</span>
          </div>
        )}
      </div>

      {/* Bottom info */}
      <div className="p-2.5 space-y-2">
        <div className="flex items-center justify-between gap-1">
          {isPipelineAsset ? (
            <Badge
              variant="outline"
              className="text-[10px] border-red-500/30 text-red-400/80 bg-red-500/[0.06] px-1.5 py-0"
            >
              Pipeline
            </Badge>
          ) : (
            <Badge
              variant="outline"
              className="text-[10px] border-white/10 text-white/50 bg-white/[0.04] px-1.5 py-0"
            >
              {FORMAT_LABELS[graphic.format] ? t(FORMAT_LABELS[graphic.format]) : graphic.format}
            </Badge>
          )}
          <span className="text-[10px] text-white/30">{formatBytes(graphic.fileSizeBytes)}</span>
        </div>

        {/* Rating + actions row */}
        <div className="flex items-center justify-between">
          <div className="flex gap-1">
            {!isPipelineAsset && (
              <>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onRate(graphic.id, graphic.rating === "up" ? null : "up");
                  }}
                  className={`p-1 rounded transition-colors ${graphic.rating === "up" ? "text-emerald-400" : "text-white/30 hover:text-white/60"}`}
                  title={t("card.goodCreative")}
                >
                  <ThumbsUp className="w-3.5 h-3.5" />
                </button>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onRate(graphic.id, graphic.rating === "down" ? null : "down");
                  }}
                  className={`p-1 rounded transition-colors ${graphic.rating === "down" ? "text-red-400" : "text-white/30 hover:text-white/60"}`}
                  title={t("card.badCreative")}
                >
                  <ThumbsDown className="w-3.5 h-3.5" />
                </button>
              </>
            )}
          </div>
          <div className="flex gap-1">
            {!isPipelineAsset && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onEdit(graphic);
                }}
                className="p-1 rounded text-white/30 hover:text-blue-400 transition-colors"
                title={t("card.editVariation")}
              >
                <Pencil className="w-3.5 h-3.5" />
              </button>
            )}
            {onPinToGallery && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onPinToGallery(graphic);
                }}
                className={`p-1 rounded transition-colors ${isInGallery ? "text-yellow-400 hover:text-yellow-300" : "text-white/30 hover:text-yellow-400"}`}
                title={isInGallery ? t("card.removeFromGallery") : t("card.addToGallery")}
              >
                {isInGallery ? <PinOff className="w-3.5 h-3.5" /> : <Pin className="w-3.5 h-3.5" />}
              </button>
            )}
            {onUseAsVideoFirstFrame && graphic.signedUrl && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onUseAsVideoFirstFrame(graphic);
                }}
                className="p-1 rounded text-white/30 hover:text-blue-400 transition-colors"
                title={t("card.useAsVideoFirstFrame")}
              >
                <Film className="w-3.5 h-3.5" />
              </button>
            )}
            {graphic.signedUrl && (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  void forceDownload(
                    graphic.signedUrl!,
                    graphic.filename || `graphic-${graphic.id}.png`
                  );
                }}
                className="p-1 rounded text-white/30 hover:text-white/60 transition-colors"
                title={t("card.download")}
              >
                <Download className="w-3.5 h-3.5" />
              </button>
            )}
            {!isPipelineAsset && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onDelete(graphic.id);
                }}
                className="p-1 rounded text-white/30 hover:text-red-400 transition-colors"
                title={t("card.delete")}
              >
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

type ContentTypeFilter = "all" | "organic" | "ad";

const CONTENT_TYPE_LABELS: Record<ContentTypeFilter, string> = {
  all: "contentType.all",
  organic: "contentType.organic",
  ad: "contentType.ad",
};

interface GraphicsLibraryProps {
  onEditPrompt?: (prompt: string) => void;
  forUserId?: string;
  contentType?: "organic" | "ad";
  onUseAsVideoFirstFrame?: (graphic: UserGraphic) => void;
}

export default function GraphicsLibrary({
  onEditPrompt,
  forUserId,
  contentType,
  onUseAsVideoFirstFrame,
}: GraphicsLibraryProps) {
  const { t } = useTranslation("graphicsLibrary");
  const { t: tRoot } = useTranslation();
  const { user } = useAuth();
  const isSuperAdmin = (user as any)?.role === "super_admin";
  const qc = useQueryClient();
  const { toast } = useToast();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [page, setPage] = useState(1);
  const [formatFilter, setFormatFilter] = useState<string>("all");
  const [ratingFilter, setRatingFilter] = useState<string>("all");
  const [mediaTypeFilter, setMediaTypeFilter] = useState<string>("all");
  const [contentTypeFilter, setContentTypeFilter] = useState<ContentTypeFilter>(
    contentType ?? "all"
  );
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [detailGraphic, setDetailGraphic] = useState<UserGraphic | null>(null);
  const [detailNotes, setDetailNotes] = useState("");
  const [editGraphic, setEditGraphic] = useState<UserGraphic | null>(null);
  const [editedPrompt, setEditedPrompt] = useState("");
  const [editInstructions, setEditInstructions] = useState("");
  const [generatedVariant, setGeneratedVariant] = useState<{
    imageUrl: string;
    prompt: string;
  } | null>(null);
  const [regenResolution, setRegenResolution] = useState<"0.5K" | "1K" | "2K" | "4K">("1K");
  const [includeOwn, setIncludeOwn] = useState(true);
  const [pinTargetGraphic, setPinTargetGraphic] = useState<UserGraphic | null>(null);
  const [pinCategory, setPinCategory] = useState<GalleryCategory>(GALLERY_CATEGORIES[0]);

  const params = new URLSearchParams({ page: String(page) });
  if (forUserId) params.set("forUserId", forUserId);
  if (forUserId && includeOwn) params.set("includeOwn", "true");
  if (formatFilter !== "all") params.set("format", formatFilter);
  if (ratingFilter !== "all") params.set("rating", ratingFilter);
  if (mediaTypeFilter !== "all") params.set("mediaType", mediaTypeFilter);
  if (search.trim()) params.set("search", search.trim());
  if (contentTypeFilter !== "all") params.set("contentType", contentTypeFilter);

  const { data, isLoading, isError, refetch } = useQuery<LibraryResponse>({
    queryKey: [
      "graphics-library",
      forUserId,
      includeOwn,
      page,
      formatFilter,
      ratingFilter,
      mediaTypeFilter,
      search,
      contentTypeFilter,
    ],
    queryFn: () => apiRequest("GET", `/api/graphics?${params}`),
    staleTime: 30_000,
    retry: 2,
  });

  // Stories sequences: fetched independently and rendered as carousel cards
  // alongside individual graphics. Frames belonging to a sequence are filtered
  // out of the regular grid (otherwise we'd see the same content twice).
  const { data: sequencesData } = useQuery<{ sequences: SequenceListItem[] }>({
    queryKey: ["stories-sequences", forUserId],
    queryFn: () => apiRequest("GET", "/api/stories-creator/sequences"),
    staleTime: 30_000,
    retry: 1,
  });
  const sequences = sequencesData?.sequences ?? [];
  const [openSequenceId, setOpenSequenceId] = useState<string | null>(null);

  // VP-88: group bulk-uploaded format variants of one creative (shared sessionId). Only real
  // multi-format uploads (size > 1) are grouped; everything else stays in the flat grid as today.
  const creativeGroups = useMemo(() => {
    const bySession = new Map<string, UserGraphic[]>();
    for (const g of data?.items ?? []) {
      if (!g.sessionId || !g.isUserUpload) continue;
      const arr = bySession.get(g.sessionId) ?? [];
      arr.push(g);
      bySession.set(g.sessionId, arr);
    }
    return [...bySession.values()].filter((arr) => arr.length > 1);
  }, [data]);
  const groupedIds = useMemo(
    () => new Set(creativeGroups.flat().map((g) => g.id)),
    [creativeGroups]
  );
  const [openGroupId, setOpenGroupId] = useState<string | null>(null);

  const quotaParams = new URLSearchParams();
  if (forUserId) quotaParams.set("forUserId", forUserId);
  const quotaUrl = quotaParams.toString()
    ? `/api/graphics/quota?${quotaParams.toString()}`
    : "/api/graphics/quota";

  const { data: quota } = useQuery<QuotaResponse>({
    queryKey: ["graphics-quota", forUserId],
    queryFn: () => apiRequest("GET", quotaUrl),
    staleTime: 60_000,
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: object }) =>
      apiRequest("PATCH", `/api/graphics/${id}`, patch),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["graphics-library"] });
      qc.invalidateQueries({ queryKey: ["graphics-quota"] });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => apiRequest("DELETE", `/api/graphics/${id}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["graphics-library"] });
      qc.invalidateQueries({ queryKey: ["graphics-quota"] });
    },
  });

  const batchDeleteMutation = useMutation({
    mutationFn: (ids: string[]) => apiRequest("DELETE", "/api/graphics/batch", { ids }),
    onSuccess: () => {
      setSelected(new Set());
      qc.invalidateQueries({ queryKey: ["graphics-library"] });
      qc.invalidateQueries({ queryKey: ["graphics-quota"] });
    },
  });

  const uploadMutation = useMutation({
    mutationFn: async (files: File[]) => {
      const formData = new FormData();
      for (const file of files) formData.append("files", file);
      if (contentType) formData.append("contentType", contentType);
      const resp = await fetch("/api/graphics/upload", {
        method: "POST",
        headers: { Authorization: `Bearer ${getAuthToken() || ""}` },
        body: formData,
      });
      if (!resp.ok) {
        const err = await resp.json().catch(() => ({ message: t("upload.error") }));
        throw new Error(err.message);
      }
      return resp.json();
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["graphics-library"] });
      qc.invalidateQueries({ queryKey: ["graphics-quota"] });
    },
  });

  const pinMutation = useMutation({
    mutationFn: ({ id, pin, category }: { id: string; pin: boolean; category: GalleryCategory }) =>
      apiRequest("POST", `/api/viral-gallery/${id}/pin`, { pin, category }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["graphics-library"] });
      qc.invalidateQueries({ queryKey: ["viral-gallery"] });
      setPinTargetGraphic(null);
    },
  });

  const handleConfirmPin = () => {
    if (!pinTargetGraphic) return;
    const willPin = !pinTargetGraphic.isInGallery;
    pinMutation.mutate({ id: pinTargetGraphic.id, pin: willPin, category: pinCategory });
  };

  const handleFileUpload = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const fileList = e.target.files;
      if (!fileList?.length) return;
      uploadMutation.mutate(Array.from(fileList));
      e.target.value = "";
    },
    [uploadMutation]
  );

  const [cloudPickerOpen, setCloudPickerOpen] = useState(false);

  const handleSelect = useCallback((id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  }, []);

  const handleRate = useCallback(
    (id: string, rating: "up" | "down" | null) => {
      updateMutation.mutate({ id, patch: { rating } });
    },
    [updateMutation]
  );

  const handleDelete = useCallback(
    (id: string) => {
      if (!confirm(t("confirmDelete"))) return;
      deleteMutation.mutate(id);
      if (detailGraphic?.id === id) setDetailGraphic(null);
    },
    [deleteMutation, detailGraphic, t]
  );

  const openDetail = (g: UserGraphic) => {
    setDetailGraphic(g);
    setDetailNotes(g.notes || "");
  };

  const openEdit = (g: UserGraphic) => {
    setEditGraphic(g);
    setEditedPrompt(g.prompt || "");
    setEditInstructions("");
    setGeneratedVariant(null);
  };

  // VP-119: regeneracja leci jako async job (202 + polling), bo synchroniczna odpowiedź
  // przebijała limit CloudFront → 504. Czekamy na zakończenie joba, potem pokazujemy wariant.
  const [activeRegenJobId, setActiveRegenJobId] = useState<string | null>(null);
  const regenerateFromLibraryMutation = useMutation({
    mutationFn: async (data: Record<string, unknown>) => {
      const resp = await apiRequest("POST", "/api/ad-creator/regenerate-graphic", data);
      return resp as { jobId: string };
    },
    onSuccess: ({ jobId }) => {
      setActiveRegenJobId(jobId);
    },
    onError: (err: Error) => {
      toast({
        title: t("editDialog.regenerateFailed", "Nie udało się wygenerować wariantu"),
        description: FriendlyErrorMapper.map(err).description,
        variant: "destructive",
      });
    },
  });
  const { job: regenJobData, pollFailure: regenPollFailure } = useJobPolling<{
    status: string;
    graphics?: Array<{ imageUrl: string; prompt: string }>;
    errorMessage?: string | null;
  }>(activeRegenJobId);
  useEffect(() => {
    if (!regenJobData || !activeRegenJobId) return;
    if (regenJobData.status === "done") {
      const variant = regenJobData.graphics?.[0];
      if (variant) setGeneratedVariant({ imageUrl: variant.imageUrl, prompt: variant.prompt });
      setActiveRegenJobId(null);
      qc.invalidateQueries({ queryKey: ["graphics-library"] });
      qc.invalidateQueries({ queryKey: ["graphics-quota"] });
    } else if (regenJobData.status === "error") {
      toast({
        title: t("editDialog.regenerateFailed", "Nie udało się wygenerować wariantu"),
        description: regenJobData.errorMessage ?? t("editDialog.tryAgain", "Spróbuj ponownie."),
        variant: "destructive",
      });
      setActiveRegenJobId(null);
    }
    // Efekt reaguje na zmianę statusu zadania i pokazuje toast — celowo wąskie zależności,
    // żeby zmiana czegokolwiek innego nie odpaliła go po raz drugi.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [regenJobData, activeRegenJobId]);
  // Polling sam w sobie może paść (timeout / błąd GET) — bez tego spinner i disabled
  // button wisiałyby wiecznie. Resetujemy stan i pokazujemy toast.
  useEffect(() => {
    if (!regenPollFailure || !activeRegenJobId) return;
    toast({
      title: t("editDialog.regenerateFailed", "Nie udało się wygenerować wariantu"),
      description:
        regenPollFailure.kind === "timeout"
          ? t("editDialog.pollTimeout", "Przekroczono czas oczekiwania — spróbuj ponownie.")
          : regenPollFailure.message,
      variant: "destructive",
    });
    setActiveRegenJobId(null);
    // Jak wyżej — toast ma polecieć dokładnie raz na nieudane pollowanie.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [regenPollFailure, activeRegenJobId]);
  // True while the regenerate POST is in flight OR its background job is still running.
  const regeneratingVariant = regenerateFromLibraryMutation.isPending || !!activeRegenJobId;

  const handleGenerateVariant = () => {
    if (!editGraphic) return;
    regenerateFromLibraryMutation.mutate({
      productDescription: editGraphic.prompt || editedPrompt,
      editInstructions: editInstructions || undefined,
      originalImageUrl: editGraphic.signedUrl || undefined,
      originalPrompt: editGraphic.prompt || undefined,
      format: editGraphic.format || "feed",
      imageModel: editGraphic.imageModel || "nano-banana-pro",
      resolution: regenResolution,
      contentType:
        contentType ??
        (contentTypeFilter === "organic"
          ? "organic"
          : contentTypeFilter === "ad"
            ? "ad"
            : undefined),
      ...(forUserId ? { forUserId } : {}),
    });
  };

  const saveNotes = () => {
    if (!detailGraphic) return;
    updateMutation.mutate({ id: detailGraphic.id, patch: { notes: detailNotes } });
    setDetailGraphic((prev) => (prev ? { ...prev, notes: detailNotes } : null));
  };

  const totalPages = data ? Math.ceil(data.total / data.pageSize) : 1;

  return (
    <div className="graphics-library-root space-y-4">
      {/* Header + quota */}
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-lg font-semibold text-foreground">{t("header.title")}</h2>
            <p className="text-xs text-muted-foreground">
              {quota
                ? `${quota.totalAssetCount ?? quota.graphicsCount} ${t("header.creativeCount")}`
                : t("header.loading")}
            </p>
          </div>
          <div className="flex items-center gap-2">
            {forUserId && (
              <button
                type="button"
                onClick={() => setIncludeOwn((v) => !v)}
                className={`text-xs px-2.5 py-1.5 rounded-lg border transition-colors ${
                  includeOwn
                    ? "bg-red-600/15 border-red-500/40 text-red-300"
                    : "bg-white/[0.03] border-white/[0.08] text-white/50 hover:text-white/80"
                }`}
                title={t("header.showOwnTooltip")}
              >
                {includeOwn ? "✓ " : ""}
                {t("header.showOwn")}
              </button>
            )}
            {selected.size > 0 && (
              <Button
                variant="destructive"
                size="sm"
                onClick={() => {
                  if (!confirm(`${t("confirmBatchDelete", { count: selected.size })}`)) return;
                  const graphicIds = Array.from(selected).filter(
                    (id) => data?.items.find((i) => i.id === id)?.source !== "asset"
                  );
                  if (graphicIds.length) batchDeleteMutation.mutate(graphicIds);
                }}
                disabled={batchDeleteMutation.isPending}
              >
                {batchDeleteMutation.isPending ? (
                  <Loader2 className="w-3 h-3 animate-spin mr-1" />
                ) : (
                  <Trash2 className="w-3 h-3 mr-1" />
                )}
                {t("header.deleteSelected", { count: selected.size })}
              </Button>
            )}
            <input
              ref={fileInputRef}
              type="file"
              multiple
              accept="image/jpeg,image/png,image/webp,image/gif,video/mp4,video/quicktime,video/x-msvideo,video/webm"
              className="hidden"
              onChange={handleFileUpload}
            />
            <Button
              size="sm"
              onClick={() => fileInputRef.current?.click()}
              disabled={uploadMutation.isPending}
              className="text-xs bg-emerald-600 hover:bg-emerald-500 text-white gap-1.5"
            >
              {uploadMutation.isPending ? (
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
              ) : (
                <Upload className="w-3.5 h-3.5" />
              )}
              {t("header.uploadButton")}
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={() => setCloudPickerOpen(true)}
              className="text-xs border-white/10 hover:border-white/20 bg-white/[0.03] text-white/60 hover:text-white gap-1.5"
            >
              <Cloud className="w-3.5 h-3.5" />
              {t("header.cloudButton")}
            </Button>
          </div>
        </div>

        {uploadMutation.isError && (
          <div className="text-xs text-red-400 bg-red-500/10 border border-red-500/20 rounded-lg px-3 py-2">
            {(uploadMutation.error as Error).message}
          </div>
        )}

        {quota && <QuotaBar used={quota.usedBytes} total={quota.quotaBytes} />}
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-2">
        <Input
          placeholder={t("filters.searchPlaceholder")}
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setPage(1);
          }}
          className="w-48 h-8 text-xs bg-white/[0.04] border-white/[0.08] text-white placeholder:text-white/30"
        />
        <Select
          value={contentTypeFilter}
          onValueChange={(v) => {
            setContentTypeFilter(v as ContentTypeFilter);
            setPage(1);
          }}
        >
          <SelectTrigger className="w-40 h-8 text-xs bg-white/[0.04] border-white/[0.08] text-white">
            <SelectValue placeholder={t("filters.contentTypePlaceholder")} />
          </SelectTrigger>
          <SelectContent className="bg-[#0d0f14] border-white/[0.08]">
            {(Object.entries(CONTENT_TYPE_LABELS) as [ContentTypeFilter, string][]).map(
              ([v, l]) => (
                <SelectItem key={v} value={v} className="text-xs text-white">
                  {t(l)}
                </SelectItem>
              )
            )}
          </SelectContent>
        </Select>
        <Select
          value={formatFilter}
          onValueChange={(v) => {
            setFormatFilter(v);
            setPage(1);
          }}
        >
          <SelectTrigger className="w-36 h-8 text-xs bg-white/[0.04] border-white/[0.08] text-white">
            <SelectValue placeholder={t("filters.formatPlaceholder")} />
          </SelectTrigger>
          <SelectContent className="bg-[#0d0f14] border-white/[0.08]">
            <SelectItem value="all" className="text-xs text-white">
              {t("filters.allFormats")}
            </SelectItem>
            {Object.entries(FORMAT_LABELS).map(([v, l]) => (
              <SelectItem key={v} value={v} className="text-xs text-white">
                {t(l)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select
          value={ratingFilter}
          onValueChange={(v) => {
            setRatingFilter(v);
            setPage(1);
          }}
        >
          <SelectTrigger className="w-36 h-8 text-xs bg-white/[0.04] border-white/[0.08] text-white">
            <SelectValue placeholder={t("filters.ratingPlaceholder")} />
          </SelectTrigger>
          <SelectContent className="bg-[#0d0f14] border-white/[0.08]">
            <SelectItem value="all" className="text-xs text-white">
              {t("filters.allRatings")}
            </SelectItem>
            <SelectItem value="up" className="text-xs text-white">
              👍 {t("filters.ratingLiked")}
            </SelectItem>
            <SelectItem value="down" className="text-xs text-white">
              👎 {t("filters.ratingRejected")}
            </SelectItem>
          </SelectContent>
        </Select>
        <Select
          value={mediaTypeFilter}
          onValueChange={(v) => {
            setMediaTypeFilter(v);
            setPage(1);
          }}
        >
          <SelectTrigger className="w-36 h-8 text-xs bg-white/[0.04] border-white/[0.08] text-white">
            <SelectValue placeholder={t("filters.mediaTypePlaceholder")} />
          </SelectTrigger>
          <SelectContent className="bg-[#0d0f14] border-white/[0.08]">
            <SelectItem value="all" className="text-xs text-white">
              <span className="flex items-center gap-1.5">{t("filters.allMediaTypes")}</span>
            </SelectItem>
            <SelectItem value="image" className="text-xs text-white">
              <span className="flex items-center gap-1.5">
                <Image className="w-3 h-3" /> {t("filters.images")}
              </span>
            </SelectItem>
            <SelectItem value="video" className="text-xs text-white">
              <span className="flex items-center gap-1.5">
                <Film className="w-3 h-3" /> {t("filters.videos")}
              </span>
            </SelectItem>
          </SelectContent>
        </Select>
        {(formatFilter !== "all" ||
          ratingFilter !== "all" ||
          mediaTypeFilter !== "all" ||
          contentTypeFilter !== (contentType ?? "all") ||
          search) && (
          <button
            onClick={() => {
              setFormatFilter("all");
              setRatingFilter("all");
              setMediaTypeFilter("all");
              setContentTypeFilter(contentType ?? "all");
              setSearch("");
              setPage(1);
            }}
            className="flex items-center gap-1 text-xs text-white/40 hover:text-white/70 transition-colors"
          >
            <X className="w-3 h-3" /> {t("filters.clearFilters")}
          </button>
        )}
      </div>

      {/* Grid */}
      {isLoading ? (
        // VP-208: siatka placeholderów w wymiarach realnych kart — bez skoku layoutu
        // po dociągnięciu grafik (wcześniej wyśrodkowany spinner na py-20).
        <div className="space-y-2">
          <Skeleton className="h-3 w-40" />
          <MediaGridSkeleton count={12} />
        </div>
      ) : isError ? (
        <div className="flex flex-col items-center justify-center py-20 gap-3 text-center">
          <RefreshCw className="w-10 h-10 text-red-400/40" />
          <p className="text-white/50 text-sm">{t("states.loadError")}</p>
          <Button variant="outline" size="sm" onClick={() => refetch()} className="text-xs">
            {t("states.retry")}
          </Button>
        </div>
      ) : !data?.items.length ? (
        <div className="flex flex-col items-center justify-center py-20 gap-4 text-center">
          <div className="w-14 h-14 rounded-2xl bg-white/[0.04] flex items-center justify-center">
            <ImageOff className="w-7 h-7 text-white/30" />
          </div>
          <div className="space-y-1">
            <p className="text-white/70 text-sm font-medium">{t("states.empty")}</p>
            <p className="text-white/40 text-xs max-w-xs">
              {contentTypeFilter === "ad" ? t("states.emptyAdHint") : t("states.emptyOrganicHint")}
            </p>
          </div>
          <p className="text-[10px] text-white/30 mt-2">
            Wszystkie wygenerowane i wgrane materiały lądują tutaj — pogrupowane wg formatu i z
            AI-tagami
          </p>
        </div>
      ) : (
        <div className="space-y-5">
          {sequences.length > 0 && (
            <div className="space-y-2">
              <div className="text-[11px] uppercase tracking-wider text-white/50 font-semibold px-1">
                Sekwencje Stories ({sequences.length})
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-3">
                {sequences.map((s) => (
                  <SequenceCard key={s.id} sequence={s} onOpen={setOpenSequenceId} />
                ))}
              </div>
            </div>
          )}
          {creativeGroups.length > 0 && (
            <div className="space-y-2">
              <div className="text-[11px] uppercase tracking-wider text-white/50 font-semibold px-1">
                Kreacje – warianty formatów ({creativeGroups.length})
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-3">
                {creativeGroups.map((grp) => (
                  <CreativeGroupCard
                    key={grp[0].sessionId!}
                    group={grp}
                    active={openGroupId === grp[0].sessionId}
                    onOpen={(id) => setOpenGroupId((cur) => (cur === id ? null : id))}
                  />
                ))}
              </div>
              {openGroupId && (
                <div className="rounded-lg border border-white/10 bg-white/[0.02] p-3 space-y-2">
                  <div className="flex items-center justify-between">
                    <div className="text-xs text-white/60">Formaty tej kreacji</div>
                    <button
                      type="button"
                      onClick={() => setOpenGroupId(null)}
                      className="text-white/40 hover:text-white"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                  <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3">
                    {(creativeGroups.find((grp) => grp[0].sessionId === openGroupId) ?? []).map(
                      (g) => (
                        <GraphicCard
                          key={g.id}
                          graphic={g}
                          selected={selected.has(g.id)}
                          onSelect={handleSelect}
                          onOpen={openDetail}
                          onDelete={handleDelete}
                          onRate={handleRate}
                          onEdit={openEdit}
                          onPinToGallery={isSuperAdmin ? setPinTargetGraphic : undefined}
                          isInGallery={g.isInGallery ?? false}
                          onUseAsVideoFirstFrame={onUseAsVideoFirstFrame}
                        />
                      )
                    )}
                  </div>
                </div>
              )}
            </div>
          )}
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3">
            {data.items
              .filter((g) => g.funnelType !== "stories_sequence" && !groupedIds.has(g.id))
              .map((g) => (
                <GraphicCard
                  key={g.id}
                  graphic={g}
                  selected={selected.has(g.id)}
                  onSelect={handleSelect}
                  onOpen={openDetail}
                  onDelete={handleDelete}
                  onRate={handleRate}
                  onEdit={openEdit}
                  onPinToGallery={isSuperAdmin ? setPinTargetGraphic : undefined}
                  isInGallery={g.isInGallery ?? false}
                  onUseAsVideoFirstFrame={onUseAsVideoFirstFrame}
                />
              ))}
          </div>
        </div>
      )}

      {openSequenceId && (
        <SequenceDetailModal sequenceId={openSequenceId} onClose={() => setOpenSequenceId(null)} />
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-3 pt-2">
          <button
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page <= 1}
            className="pagination-btn p-1.5 rounded text-white/40 hover:text-white/70 disabled:opacity-30 transition-colors"
          >
            <ChevronLeft className="w-4 h-4" />
          </button>
          <span className="pagination-label text-xs text-white/50">
            {page} / {totalPages}
          </span>
          <button
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            disabled={page >= totalPages}
            className="pagination-btn p-1.5 rounded text-white/40 hover:text-white/70 disabled:opacity-30 transition-colors"
          >
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Edit prompt dialog */}
      <Dialog
        open={!!editGraphic}
        onOpenChange={(open) => {
          if (!open) {
            setEditGraphic(null);
            setGeneratedVariant(null);
          }
        }}
      >
        <DialogContent className="bg-[#0d0f14] border-white/[0.08] text-white max-w-xl">
          <DialogHeader>
            <DialogTitle className="text-sm font-medium text-white/80 flex items-center gap-2">
              <Pencil className="w-4 h-4 text-blue-400" />
              {t("editDialog.title")}
            </DialogTitle>
          </DialogHeader>
          {editGraphic && (
            <div className="space-y-4">
              <div className="flex gap-3 items-start">
                {editGraphic.signedUrl && (
                  <img
                    src={editGraphic.signedUrl}
                    alt="preview"
                    className="w-20 h-20 object-cover rounded-lg shrink-0"
                  />
                )}
                <div className="text-xs text-white/40 leading-relaxed">
                  <span className="text-white/60 font-medium">
                    {FORMAT_LABELS[editGraphic.format]
                      ? t(FORMAT_LABELS[editGraphic.format])
                      : editGraphic.format}
                  </span>
                  {" · "}v{editGraphic.variantIndex}
                  {" · "}
                  {editGraphic.imageModel || ""}
                </div>
              </div>

              {editGraphic.prompt && (
                <div>
                  <div className="text-xs text-white/40 mb-1">{t("editDialog.originalPrompt")}</div>
                  <div className="text-xs text-white/50 bg-white/[0.03] border border-white/[0.06] rounded-lg p-2.5 max-h-20 overflow-y-auto leading-relaxed">
                    {editGraphic.prompt.slice(0, 500)}
                    {editGraphic.prompt.length > 500 ? "…" : ""}
                  </div>
                </div>
              )}

              <div>
                <div className="text-xs text-white/40 mb-1.5">{t("editDialog.whatToChange")}</div>
                <Textarea
                  value={editInstructions}
                  onChange={(e) => setEditInstructions(e.target.value)}
                  placeholder={t("editDialog.whatToChangePlaceholder")}
                  className="text-xs bg-white/[0.04] border-white/[0.08] text-white placeholder:text-white/30 resize-none h-24"
                />
                {(() => {
                  const editModel = editGraphic.imageModel || "nano-banana-pro";
                  const isEditCapable = [
                    "nano-banana-pro",
                    "nano-banana-2",
                    "seedream-4.5",
                    "seedream-5.0-lite",
                    "seedream-4.0",
                    "gpt-image-1",
                    "gpt-image-2",
                  ].includes(editModel);
                  if (isEditCapable)
                    return (
                      <p className="text-[10px] text-emerald-400/60 mt-1 flex items-center gap-1">
                        <Pencil className="w-2.5 h-2.5" />{" "}
                        {t("editDialog.editCapable", { model: editModel })}
                      </p>
                    );
                  return (
                    <p className="text-[10px] text-white/30 mt-1">
                      {t("editDialog.noEditSupport")}
                    </p>
                  );
                })()}
              </div>

              {/* Resolution selector for regeneration */}
              <div>
                <div className="text-xs text-white/40 mb-1.5">{t("editDialog.resolution")}</div>
                <div className="flex gap-1.5">
                  {(["1K", "2K", "4K"] as const).map((r) => (
                    <button
                      key={r}
                      onClick={() => setRegenResolution(r)}
                      className={`px-2.5 py-1 rounded-lg border text-xs font-medium transition-all ${
                        regenResolution === r
                          ? "bg-blue-600/15 border-blue-500/40 text-blue-300"
                          : "bg-white/[0.03] border-white/[0.08] text-white/60 hover:text-white hover:border-white/[0.15]"
                      }`}
                    >
                      {r}
                      <span
                        className={`ml-1 text-[8px] font-bold px-1 py-0.5 rounded ${
                          r === "1K"
                            ? "bg-white/5 text-white/40"
                            : r === "2K"
                              ? "bg-amber-500/20 text-amber-400"
                              : "bg-orange-500/20 text-orange-400"
                        }`}
                      >
                        {r === "1K" ? "1x" : r === "2K" ? "1.5x" : "2x"}
                      </span>
                    </button>
                  ))}
                </div>
              </div>

              {generatedVariant && (
                <div className="border border-emerald-500/20 bg-emerald-500/[0.04] rounded-lg p-3 space-y-2">
                  <div className="text-xs text-emerald-400/80 font-medium">
                    {t("editDialog.generatedVariant")}
                  </div>
                  <img
                    src={generatedVariant.imageUrl}
                    alt="variant"
                    className="w-full max-h-60 object-contain rounded-lg"
                  />
                </div>
              )}

              <div className="flex items-center justify-end gap-2">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => {
                    setEditGraphic(null);
                    setGeneratedVariant(null);
                  }}
                  className="text-xs border-white/[0.1] bg-white/[0.04] text-white hover:bg-white/[0.08]"
                >
                  {generatedVariant ? t("editDialog.close") : t("editDialog.cancel")}
                </Button>
                <Button
                  size="sm"
                  onClick={handleGenerateVariant}
                  disabled={!editInstructions.trim() || regeneratingVariant}
                  className="text-xs bg-blue-600 hover:bg-blue-500 text-white gap-1.5"
                >
                  {regeneratingVariant ? (
                    <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  ) : (
                    <RefreshCw className="w-3.5 h-3.5" />
                  )}
                  {t("editDialog.generateVariant")}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Detail side panel */}
      {detailGraphic && (
        <div className="fixed inset-0 z-[60] pointer-events-none">
          {/* Overlay */}
          <div
            className="absolute inset-0 bg-black/50 pointer-events-auto"
            onClick={() => setDetailGraphic(null)}
          />
          {/* Panel */}
          <div className="absolute right-0 top-0 h-full w-[420px] bg-[#0d0f14] border-l border-white/[0.08] text-white flex flex-col pointer-events-auto shadow-2xl">
            <div className="flex items-center justify-between px-5 pt-5 pb-3 border-b border-white/[0.06] shrink-0">
              <span className="text-sm font-medium text-white/80">
                {`${FORMAT_LABELS[detailGraphic.format] ? t(FORMAT_LABELS[detailGraphic.format]) : detailGraphic.format} · v${detailGraphic.variantIndex}`}
              </span>
              <button
                onClick={() => setDetailGraphic(null)}
                className="p-1.5 rounded-lg text-white/40 hover:text-white hover:bg-white/[0.08] transition-colors"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            {detailGraphic && (
              <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
                {/* Media preview */}
                {detailGraphic.signedUrl && (
                  <div className="flex justify-center bg-black/40 rounded-xl overflow-hidden">
                    {isVideoFile(detailGraphic) ? (
                      <video
                        src={detailGraphic.signedUrl}
                        className="w-full max-h-80 object-contain"
                        controls
                        preload="metadata"
                      />
                    ) : (
                      <img
                        src={detailGraphic.signedUrl}
                        alt="preview"
                        className="w-full object-contain"
                      />
                    )}
                  </div>
                )}

                {/* Action buttons row */}
                <div className="grid grid-cols-2 gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={saveNotes}
                    disabled={updateMutation.isPending}
                    className="text-xs border-white/[0.1] bg-white/[0.04] text-white hover:bg-white/[0.08] gap-1.5"
                  >
                    {updateMutation.isPending ? <Loader2 className="w-3 h-3 animate-spin" /> : null}
                    {t("detail.saveNotes")}
                  </Button>
                  {detailGraphic.signedUrl && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() =>
                        forceDownload(
                          detailGraphic.signedUrl!,
                          detailGraphic.filename || `graphic-${detailGraphic.id}.png`
                        )
                      }
                      className="w-full text-xs border-white/[0.1] bg-white/[0.04] text-white hover:bg-white/[0.08] gap-1.5"
                    >
                      <Download className="w-3 h-3" /> {t("detail.download")}
                    </Button>
                  )}
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => {
                      setDetailGraphic(null);
                      openEdit(detailGraphic);
                    }}
                    className="text-xs border-white/[0.1] bg-blue-500/10 text-blue-300 hover:bg-blue-500/20 hover:text-blue-200 gap-1.5"
                  >
                    <Pencil className="w-3 h-3" /> {t("detail.edit")}
                  </Button>
                  <Button
                    size="sm"
                    variant="destructive"
                    onClick={() => handleDelete(detailGraphic.id)}
                    disabled={deleteMutation.isPending}
                    className="text-xs gap-1.5"
                  >
                    {deleteMutation.isPending ? (
                      <Loader2 className="w-3 h-3 animate-spin" />
                    ) : (
                      <Trash2 className="w-3 h-3" />
                    )}
                    {t("detail.delete")}
                  </Button>
                </div>

                {/* Rating */}
                <div className="flex items-center gap-2">
                  <span className="text-xs text-white/40">{t("detail.ratingLabel")}</span>
                  <button
                    onClick={() =>
                      handleRate(detailGraphic.id, detailGraphic.rating === "up" ? null : "up")
                    }
                    className={`p-1.5 rounded-lg border transition-colors ${
                      detailGraphic.rating === "up"
                        ? "bg-emerald-500/20 border-emerald-500/40 text-emerald-400"
                        : "bg-white/[0.04] border-white/[0.08] text-white/40 hover:text-emerald-400"
                    }`}
                  >
                    <ThumbsUp className="w-3.5 h-3.5" />
                  </button>
                  <button
                    onClick={() =>
                      handleRate(detailGraphic.id, detailGraphic.rating === "down" ? null : "down")
                    }
                    className={`p-1.5 rounded-lg border transition-colors ${
                      detailGraphic.rating === "down"
                        ? "bg-red-500/20 border-red-500/40 text-red-400"
                        : "bg-white/[0.04] border-white/[0.08] text-white/40 hover:text-red-400"
                    }`}
                  >
                    <ThumbsDown className="w-3.5 h-3.5" />
                  </button>
                </div>

                {/* Meta info */}
                <div className="grid grid-cols-2 gap-2 text-xs">
                  <div className="bg-white/[0.04] rounded-lg px-3 py-2">
                    <div className="text-white/40 mb-0.5">{t("detail.modelLabel")}</div>
                    <div className="text-white/80 truncate">{detailGraphic.imageModel || "—"}</div>
                  </div>
                  <div className="bg-white/[0.04] rounded-lg px-3 py-2">
                    <div className="text-white/40 mb-0.5">{t("detail.sizeLabel")}</div>
                    <div className="text-white/80">
                      {detailGraphic.width}×{detailGraphic.height} ·{" "}
                      {formatBytes(detailGraphic.fileSizeBytes)}
                    </div>
                  </div>
                  <div className="bg-white/[0.04] rounded-lg px-3 py-2">
                    <div className="text-white/40 mb-0.5">{t("detail.formatLabel")}</div>
                    <div className="text-white/80">
                      {FORMAT_LABELS[detailGraphic.format]
                        ? t(FORMAT_LABELS[detailGraphic.format])
                        : detailGraphic.format}
                    </div>
                  </div>
                  <div className="bg-white/[0.04] rounded-lg px-3 py-2">
                    <div className="text-white/40 mb-0.5">{t("detail.dateLabel")}</div>
                    <div className="text-white/80">
                      {new Date(detailGraphic.createdAt).toLocaleDateString("pl-PL")}
                    </div>
                  </div>
                </div>

                {/* Prompt */}
                {detailGraphic.prompt && (
                  <div>
                    <div className="text-xs text-white/40 mb-1">{t("detail.promptLabel")}</div>
                    <p className="text-xs text-white/60 bg-white/[0.04] rounded-lg p-2.5 max-h-32 overflow-y-auto leading-relaxed">
                      {/* VP-179: część grafik ma zapisaną strukturę JSON — pokazujemy streszczenie. */}
                      {summarizeImagePrompt(detailGraphic.prompt)}
                    </p>
                  </div>
                )}

                {/* Notes */}
                <div>
                  <div className="text-xs text-white/40 mb-1">{t("detail.notesLabel")}</div>
                  <Textarea
                    value={detailNotes}
                    onChange={(e) => setDetailNotes(e.target.value)}
                    placeholder={t("detail.notesPlaceholder")}
                    className="text-xs bg-white/[0.04] border-white/[0.08] text-white placeholder:text-white/30 resize-none h-20"
                  />
                </div>

                {/* Actions */}
                <div className="flex items-center justify-between">
                  <div className="flex gap-2">
                    {detailGraphic.source !== "asset" && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={saveNotes}
                        disabled={updateMutation.isPending}
                        className="text-xs border-white/[0.1] bg-white/[0.04] text-white hover:bg-white/[0.08]"
                      >
                        Zapisz notatki
                      </Button>
                    )}
                    {detailGraphic.signedUrl && (
                      <a
                        href={detailGraphic.signedUrl}
                        download={detailGraphic.filename || `graphic-${detailGraphic.id}.png`}
                      >
                        <Button
                          size="sm"
                          variant="outline"
                          className="text-xs border-white/[0.1] bg-white/[0.04] text-white hover:bg-white/[0.08]"
                        >
                          <Download className="w-3 h-3 mr-1" /> {t("detail.download")}
                        </Button>
                      </a>
                    )}
                  </div>
                  {detailGraphic.source !== "asset" && (
                    <Button
                      size="sm"
                      variant="destructive"
                      onClick={() => handleDelete(detailGraphic.id)}
                      disabled={deleteMutation.isPending}
                    >
                      <Trash2 className="w-3 h-3 mr-1" /> {t("detail.delete")}
                    </Button>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      <CloudStoragePicker
        open={cloudPickerOpen}
        onClose={() => setCloudPickerOpen(false)}
        onFileImported={() => {
          qc.invalidateQueries({ queryKey: ["graphics-library"] });
          qc.invalidateQueries({ queryKey: ["graphics-quota"] });
        }}
        accept="all"
      />

      {/* Superadmin: pin to gallery modal */}
      <Dialog
        open={!!pinTargetGraphic}
        onOpenChange={(open) => {
          if (!open) setPinTargetGraphic(null);
        }}
      >
        <DialogContent className="bg-[#0d0f14] border-white/[0.08] text-white max-w-sm">
          <DialogHeader>
            <DialogTitle className="text-sm font-medium text-white/80 flex items-center gap-2">
              <GalleryVertical className="w-4 h-4 text-yellow-400" />
              {pinTargetGraphic?.isInGallery
                ? t("galleryPin.removeTitle")
                : t("galleryPin.addTitle")}
            </DialogTitle>
          </DialogHeader>
          {pinTargetGraphic && (
            <div className="space-y-4">
              {pinTargetGraphic.signedUrl && (
                <div className="flex justify-center bg-black/20 rounded-lg overflow-hidden h-32">
                  {pinTargetGraphic.mediaType === "video" ? (
                    <video
                      src={pinTargetGraphic.signedUrl}
                      className="h-full object-contain"
                      muted
                      preload="metadata"
                    />
                  ) : (
                    <img
                      src={pinTargetGraphic.signedUrl}
                      alt="preview"
                      className="h-full object-contain"
                    />
                  )}
                </div>
              )}

              {!pinTargetGraphic.isInGallery && (
                <div>
                  <div className="text-xs text-white/40 mb-2">{t("galleryPin.chooseCategory")}</div>
                  <div className="flex flex-col gap-1.5">
                    {GALLERY_CATEGORIES.map((cat) => (
                      <button
                        key={cat}
                        onClick={() => setPinCategory(cat)}
                        className={`text-left px-3 py-2 rounded-lg border text-xs transition-colors ${
                          pinCategory === cat
                            ? "bg-yellow-500/10 border-yellow-500/40 text-yellow-300"
                            : "bg-white/[0.03] border-white/[0.06] text-white/50 hover:text-white/80 hover:border-white/10"
                        }`}
                      >
                        {tRoot(GALLERY_CATEGORY_LABEL_KEYS[cat])}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {pinTargetGraphic.isInGallery && (
                <p className="text-xs text-white/50">
                  {t("galleryPin.currentlyInGallery", {
                    category: pinTargetGraphic.galleryCategory
                      ? tRoot(GALLERY_CATEGORY_LABEL_KEYS[pinTargetGraphic.galleryCategory])
                      : "—",
                  })}
                </p>
              )}

              <div className="flex items-center justify-end gap-2">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setPinTargetGraphic(null)}
                  className="text-xs border-white/[0.1] bg-white/[0.04] text-white hover:bg-white/[0.08]"
                >
                  {t("galleryPin.cancel")}
                </Button>
                <Button
                  size="sm"
                  onClick={handleConfirmPin}
                  disabled={pinMutation.isPending}
                  className={`text-xs gap-1.5 ${pinTargetGraphic.isInGallery ? "bg-red-600 hover:bg-red-500" : "bg-yellow-500 hover:bg-yellow-400 text-black"}`}
                >
                  {pinMutation.isPending ? (
                    <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  ) : pinTargetGraphic.isInGallery ? (
                    <PinOff className="w-3.5 h-3.5" />
                  ) : (
                    <Pin className="w-3.5 h-3.5" />
                  )}
                  {pinTargetGraphic.isInGallery
                    ? t("galleryPin.removeButton")
                    : t("galleryPin.addButton")}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
